import { PLATFORM_PERMISSIONS, TENANT_PERMISSIONS, type Permission } from './permissions';

export interface RolePreset {
  scope: 'platform' | 'tenant';
  name: string;
  permissions: Permission[];
  isPreset: true;
}

/** Seeded on first boot (MASTER_PROMPT.md §10, §13 Step E). Super Admin always holds every
 * platform permission, including ones added later - see `isSuperAdminRole`. */
export const PLATFORM_ROLE_PRESETS: Record<
  'SUPER_ADMIN' | 'SUB_ADMIN' | 'SUPPORT_ADMIN',
  RolePreset
> = {
  SUPER_ADMIN: {
    scope: 'platform',
    name: 'Super Admin',
    permissions: [...PLATFORM_PERMISSIONS],
    isPreset: true,
  },
  SUB_ADMIN: {
    scope: 'platform',
    name: 'Sub Admin',
    permissions: [
      'tenants.create',
      'tenants.read',
      'tenants.update',
      'tenants.channels.manage',
      'tenants.domains.manage',
      'tenants.branding.manage',
      'billing.plans.manage',
      'billing.plans.assign',
      'billing.simulator',
      'orders.search',
      'orders.view',
      'tickets.resend',
      'audit.view',
    ],
    isPreset: true,
  },
  SUPPORT_ADMIN: {
    scope: 'platform',
    name: 'Support Admin',
    permissions: [
      'tenants.read',
      'orders.search',
      'orders.view',
      'tickets.resend',
      'refunds.process',
      'support.impersonate',
    ],
    isPreset: true,
  },
};

export const TENANT_ROLE_PRESETS: Record<
  'OWNER' | 'MANAGER' | 'FINANCE' | 'STAFF' | 'DOOR_STAFF',
  RolePreset
> = {
  OWNER: {
    scope: 'tenant',
    name: 'Owner',
    permissions: [...TENANT_PERMISSIONS],
    isPreset: true,
  },
  MANAGER: {
    scope: 'tenant',
    name: 'Manager',
    permissions: [
      'events.manage',
      'events.publish',
      'sessions.manage',
      'ticket_types.manage',
      'seat_maps.manage',
      'promo_codes.manage',
      'add_ons.manage',
      'custom_questions.manage',
      'waitlists.manage',
      'orders.view',
      'orders.refund',
      'orders.export',
      'tickets.resend',
      'tickets.check_in',
      'tickets.transfer',
      'reports.view',
    ],
    isPreset: true,
  },
  FINANCE: {
    scope: 'tenant',
    name: 'Finance',
    permissions: ['reports.view', 'payouts.view', 'invoices.view', 'orders.view', 'orders.export'],
    isPreset: true,
  },
  STAFF: {
    scope: 'tenant',
    name: 'Staff',
    permissions: ['orders.view', 'tickets.resend', 'tickets.check_in'],
    isPreset: true,
  },
  DOOR_STAFF: {
    scope: 'tenant',
    name: 'Door Staff',
    permissions: ['tickets.check_in'],
    isPreset: true,
  },
};

export function isSuperAdminRole(roleName: string): boolean {
  return roleName === PLATFORM_ROLE_PRESETS.SUPER_ADMIN.name;
}
